import { useCallback, useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { routeArrowPoints } from "@/lib/route-arrows";

interface UseRouteAnimationProps {
  map: React.MutableRefObject<maplibregl.Map | null>;
}

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };

// Reveals a picked route like the standalone HTML artifact: the orange `route`
// line (and its direction arrows) grows from start to finish with a moving head
// dot. It draws straight into the real `route`/`route-arrows` sources rather
// than a separate trail, so nothing is hidden and swapped and the full route
// never flashes before it draws. `onComplete` fires once the reveal lands, so
// the caller can commit the geometry to routing state at that point.
export const useRouteAnimation = ({ map }: UseRouteAnimationProps) => {
  const rafRef = useRef<number | null>(null);

  // getSource throws only before the style exists or after teardown. Do NOT gate
  // on isStyleLoaded(): it is also false whenever tiles are mid-load, which
  // would skip the cleanup and leave the head dot on the map.
  const clearPoint = useCallback(() => {
    const m = map.current;
    if (!m) return;
    try {
      (
        m.getSource("route-animation-point") as
          | maplibregl.GeoJSONSource
          | undefined
      )?.setData(EMPTY_FC);
    } catch {
      // style not created yet or map torn down; nothing to clear.
    }
  }, [map]);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    clearPoint();
  }, [clearPoint]);

  const play = useCallback(
    (coords: [number, number][], onComplete?: () => void) => {
      const m = map.current;
      if (!m || !coords || coords.length < 2) {
        onComplete?.();
        return;
      }
      // A second pick must not leave a prior loop running over the same sources.
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

      let route: maplibregl.GeoJSONSource | undefined;
      let arrows: maplibregl.GeoJSONSource | undefined;
      let point: maplibregl.GeoJSONSource | undefined;
      try {
        route = m.getSource("route") as maplibregl.GeoJSONSource | undefined;
        arrows = m.getSource("route-arrows") as
          | maplibregl.GeoJSONSource
          | undefined;
        point = m.getSource("route-animation-point") as
          | maplibregl.GeoJSONSource
          | undefined;
      } catch {
        onComplete?.();
        return;
      }
      if (!route || !arrows || !point) {
        onComplete?.();
        return;
      }

      const n = coords.length;
      const duration = Math.min(1500 + n * 7, 9000);
      let start: number | null = null;

      const step = (ts: number) => {
        if (start === null) start = ts;
        const progress = Math.min((ts - start) / duration, 1);
        // At least two points so the growing slice is always a drawable line.
        const idx = Math.max(2, Math.floor(progress * n));
        const slice = coords.slice(0, idx);
        route.setData({
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: slice },
        });
        arrows.setData(routeArrowPoints(slice));
        point.setData({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              // Clamp so the final frame is never coords[n] (undefined).
              geometry: {
                type: "Point",
                coordinates: coords[Math.min(idx, n - 1)],
              },
            },
          ],
        });
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
          clearPoint();
          onComplete?.();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [map, clearPoint],
  );

  useEffect(() => () => stop(), [stop]);

  return { play, stop };
};
